import { useEffect, useState, useCallback } from "react";
import { Key } from "lucide-react";
import { theme, card, btnPrimary, inputStyle, floatingSheet, emptyFrame } from "../theme";
import { api } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ApiKey {
  id: string;
  name: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  status: string;
  prefix: string;
}

interface Webhook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  lastDeliveryStatus: string | null;
  createdAt: string;
}

interface CreateKeyResult {
  key: ApiKey;
  rawKey: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const ALL_SCOPES = [
  { id: "read:org",               label: "Read: Organisation" },
  { id: "read:compliance",        label: "Read: Compliance Summary" },
  { id: "read:risks",             label: "Read: Risk Register" },
  { id: "read:documents",         label: "Read: Documents" },
];

const ALL_EVENTS = [
  { id: "compliance.score_updated",   label: "Compliance score updated" },
  { id: "risk.created",               label: "Risk created" },
  { id: "breach.reported",            label: "Breach reported" },
  { id: "dsr.created",                label: "DSR received" },
  { id: "dora.alert.raised",          label: "DORA monitor alert raised" },
  { id: "dora.alert.resolved",        label: "DORA monitor alert resolved" },
  { id: "incident.reported",          label: "Cyber incident recorded" },
  { id: "incident.notification.due",  label: "Incident notification due within 4 hours" },
  { id: "incident.notification.overdue", label: "Incident notification overdue" },
  { id: "advisory.exposure.detected",  label: "Threat advisory affects an asset (vulnerability exposure)" },
  { id: "advisory.exposure.resolved",  label: "Vulnerability exposure resolved" },
  { id: "remediation.requested",       label: "Remediation requested (ticket)" },
  { id: "remediation.completed",       label: "Remediation run finished" },
  { id: "people.joiner",               label: "Joiner recorded" },
  { id: "people.leaver",               label: "Leaver recorded" },
];

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active:  { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  revoked: { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  expired: { bg: "var(--c-faint)",     text: "var(--c-muted)" },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function Pill({ label, colors }: { label: string; colors: { bg: string; text: string } }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 3,
      fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, whiteSpace: "nowrap",
      textTransform: "capitalize",
    }}>
      {label}
    </span>
  );
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function MultiSelect({
  options,
  selected,
  onChange,
}: {
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (updated: string[]) => void;
}) {
  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map((opt) => {
        const on = selected.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => toggle(opt.id)}
            style={{
              padding: "4px 12px", borderRadius: 3, fontSize: 12,
              border: on ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
              background: on ? "var(--c-accentBg)" : "var(--c-panel)",
              color: on ? "var(--c-accentText)" : theme.color.muted,
              fontWeight: on ? 600 : 400, cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ ...floatingSheet, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--c-line)" }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{title}</div>
      <button onClick={onClose} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
    </div>
  );
}

// ── Create Key Modal ───────────────────────────────────────────────────────────

function CreateKeyModal({ onSave, onClose, saving }: {
  onSave: (name: string, scopes: string[], expiresAt: string | null) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["read:org", "read:compliance"]);
  const [expiry, setExpiry] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave(name, scopes, expiry || null);
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Create API Key" onClose={onClose} />
      <form onSubmit={handleSubmit} style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
          <span style={{ fontWeight: 500, color: theme.color.text }}>Key name *</span>
          <input required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Production dashboard" style={{ ...inputStyle, fontSize: 13 }} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
          <span style={{ fontWeight: 500, color: theme.color.text }}>Scopes</span>
          <MultiSelect options={ALL_SCOPES} selected={scopes} onChange={setScopes} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
          <span style={{ fontWeight: 500, color: theme.color.text }}>Expiry date (optional)</span>
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
            style={{ ...inputStyle, fontSize: 13 }} />
        </label>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving || scopes.length === 0}
            style={{ ...btnPrimary, opacity: saving || scopes.length === 0 ? 0.6 : 1, cursor: saving || scopes.length === 0 ? "not-allowed" : "pointer" }}>
            {saving ? "Creating…" : "Create Key"}
          </button>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Raw Key Dialog ─────────────────────────────────────────────────────────────

function RawKeyDialog({ rawKey, onClose }: { rawKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(rawKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <ModalOverlay>
      <ModalHeader title="Your new API key" onClose={onClose} />
      <div style={{ padding: 20 }}>
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", borderRadius: 3, fontSize: 12, fontWeight: 600, color: "var(--c-dangerText)" }}>
          This key will not be shown again. Copy it now and store it securely.
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 10, lineHeight: 1.5 }}>
          Use this key in the <code style={{ background: "var(--c-panelAlt)", padding: "1px 5px", borderRadius: 3 }}>Authorization</code> header of all API requests.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
          <input
            readOnly
            value={rawKey}
            onFocus={(e) => e.target.select()}
            style={{ ...inputStyle, fontSize: 12, fontFamily: "monospace", flex: 1 }}
          />
          <button
            onClick={handleCopy}
            style={{ padding: "8px 14px", fontSize: 12, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text, cursor: "pointer", flexShrink: 0, fontWeight: 600 }}>
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <button onClick={onClose} style={{ ...btnPrimary, cursor: "pointer" }}>
          Done — I've saved my key
        </button>
      </div>
    </ModalOverlay>
  );
}

// ── Add Webhook Modal ──────────────────────────────────────────────────────────

// With `initial`, the same modal edits an existing webhook's URL and events (its secret stays).
function AddWebhookModal({ onSave, onClose, saving, initial }: {
  onSave: (url: string, events: string[]) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  initial?: Webhook;
}) {
  const [url, setUrl] = useState(initial?.url ?? "");
  const [events, setEvents] = useState<string[]>(initial?.events ?? []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave(url, events);
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={initial ? "Edit Webhook" : "Add Webhook"} onClose={onClose} />
      <form onSubmit={handleSubmit} style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
          <span style={{ fontWeight: 500, color: theme.color.text }}>Endpoint URL *</span>
          <input required type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-server.com/webhook" style={{ ...inputStyle, fontSize: 13 }} />
          {url && !url.startsWith("https://") && (
            <span style={{ fontSize: 11, color: "var(--c-warningText)" }}>HTTPS is required for webhook endpoints</span>
          )}
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
          <span style={{ fontWeight: 500, color: theme.color.text }}>Events to subscribe</span>
          <MultiSelect options={ALL_EVENTS} selected={events} onChange={setEvents} />
        </label>
        <div style={{ fontSize: 12, color: theme.color.subtle, padding: "10px 12px", background: "var(--c-panelAlt)", borderRadius: 3, lineHeight: 1.5 }}>
          {initial ? "The signing secret is unchanged, so your receiver keeps verifying with the one it holds. " : "A signing secret will be generated on save. "}Verify webhook authenticity using{" "}
          <code style={{ background: "var(--c-faint)", padding: "1px 4px", borderRadius: 3 }}>X-Abacas-Signature</code>.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving || events.length === 0 || !url.startsWith("https://")}
            style={{ ...btnPrimary, opacity: saving || events.length === 0 || !url.startsWith("https://") ? 0.6 : 1, cursor: "pointer" }}>
            {saving ? "Saving…" : initial ? "Save changes" : "Add Webhook"}
          </button>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function PublicApiPage() {
  const { user } = useCurrentUser();
  // Keys and webhooks are managed by integration administrators (the server enforces integrations:admin on every route here).
  const blocked = !!user && !user.permissions?.includes("integrations:admin");
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreateKey, setShowCreateKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [newRawKey, setNewRawKey] = useState<string | null>(null);

  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState<Record<string, boolean>>({});
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; statusCode: number | null }>>({});

  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (blocked) { setLoading(false); return; }
    setLoading(true);
    try {
      const [keysData, webhooksData] = await Promise.all([
        api.get<{ keys: ApiKey[] }>("/api-keys").catch(() => ({ keys: [] })),
        api.get<{ webhooks: Webhook[] }>("/api-keys/webhooks").catch(() => ({ webhooks: [] })),
      ]);
      setApiKeys(keysData.keys ?? []);
      setWebhooks(webhooksData.webhooks ?? []);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [blocked]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function handleCreateKey(name: string, scopes: string[], expiresAt: string | null) {
    setSavingKey(true);
    setError(null);
    try {
      const result = await api.post<CreateKeyResult>("/api-keys", { name, scopes, expiresAt });
      setNewRawKey(result.rawKey);
      setShowCreateKey(false);
      await loadAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to create key. Your organisation may not have API access yet.");
    } finally {
      setSavingKey(false);
    }
  }

  async function handleRevokeKey(id: string, name: string) {
    if (!confirm(`Revoke key "${name}"? This cannot be undone.`)) return;
    try {
      await api.del(`/api-keys/${id}`);
      await loadAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to revoke key");
    }
  }

  async function handleAddWebhook(url: string, events: string[]) {
    setSavingWebhook(true);
    setError(null);
    try {
      await api.post("/api-keys/webhooks", { url, events });
      setShowAddWebhook(false);
      await loadAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to add webhook");
    } finally {
      setSavingWebhook(false);
    }
  }

  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  async function handleEditWebhook(url: string, events: string[]) {
    if (!editingWebhook) return;
    setSavingWebhook(true);
    setError(null);
    try {
      await api.patch(`/api-keys/webhooks/${editingWebhook.id}`, { url, events });
      setEditingWebhook(null);
      await loadAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update webhook");
    } finally {
      setSavingWebhook(false);
    }
  }

  async function handleToggleWebhook(id: string, active: boolean) {
    try {
      await api.patch(`/api-keys/webhooks/${id}`, { active });
      await loadAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update webhook");
    }
  }

  async function handleTestWebhook(id: string) {
    setTestingWebhook((prev) => ({ ...prev, [id]: true }));
    try {
      const result = await api.post<{ ok: boolean; statusCode: number | null; success: boolean }>(
        `/api-keys/webhooks/${id}/test`
      );
      setTestResults((prev) => ({ ...prev, [id]: { success: result.success, statusCode: result.statusCode } }));
    } catch {
      setTestResults((prev) => ({ ...prev, [id]: { success: false, statusCode: null } }));
    } finally {
      setTestingWebhook((prev) => ({ ...prev, [id]: false }));
    }
  }

  async function handleDeleteWebhook(id: string, url: string) {
    if (!confirm(`Delete webhook for ${url}?`)) return;
    try {
      await api.del(`/api-keys/webhooks/${id}`);
      await loadAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete webhook");
    }
  }

  return (
    <div className="page-pad">
      {showCreateKey && (
        <CreateKeyModal
          onSave={handleCreateKey}
          onClose={() => setShowCreateKey(false)}
          saving={savingKey}
        />
      )}
      {newRawKey && (
        <RawKeyDialog rawKey={newRawKey} onClose={() => setNewRawKey(null)} />
      )}
      {showAddWebhook && (
        <AddWebhookModal
          onSave={handleAddWebhook}
          onClose={() => setShowAddWebhook(false)}
          saving={savingWebhook}
        />
      )}
      {editingWebhook && (
        <AddWebhookModal
          initial={editingWebhook}
          onSave={handleEditWebhook}
          onClose={() => setEditingWebhook(null)}
          saving={savingWebhook}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Key size={22} color={theme.color.heading} />
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
          Public API
        </h1>
      </div>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle }}>
        Manage API keys and webhook endpoints for programmatic access to your compliance data.
      </p>

      {/* Private beta banner */}
      <div style={{ ...card, marginBottom: 24, padding: "14px 18px", background: "var(--c-accentBg)", border: "1px solid var(--c-accent)", borderRadius: 3 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-accentText)", marginBottom: 4 }}>Private beta</div>
        <div style={{ fontSize: 12, color: "var(--c-accentText)", lineHeight: 1.6, opacity: 0.9 }}>
          The Abacas Public API is currently in private beta. API key management will be enabled when access is granted for your organisation.
          Contact <a href="mailto:sales@abacassecurity.com" style={{ color: "var(--c-accentText)" }}>sales@abacassecurity.com</a> to request access.
        </div>
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {blocked && (
        <div style={{ ...card, marginBottom: 20, fontSize: 13, color: theme.color.subtle }}>
          Managing API keys and webhooks needs integration administration. Ask an organisation administrator to give you that access, or to create the key for you.
        </div>
      )}

      {!blocked && (<>
      {/* ── API Keys ── */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>API Keys</div>
          <button onClick={() => { setError(null); setShowCreateKey(true); }}
            style={{ ...btnPrimary, fontSize: 12, cursor: "pointer" }}>
            + Create API Key
          </button>
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Keys grant programmatic read access to your organisation's compliance data.
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>
        ) : apiKeys.length === 0 ? (
          <div style={{ ...emptyFrame, padding: "24px 0", fontSize: 13, color: theme.color.faint }}>
            No API keys yet. Create your first key to get started.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
                  {["Name", "Prefix", "Scopes", "Created", "Last used", "Expires", "Status", ""].map((h) => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: 11, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key, i) => (
                  <tr key={key.id} style={{ borderBottom: i < apiKeys.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: theme.color.heading }}>{key.name}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 12, color: theme.color.muted }}>{key.prefix}…</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {key.scopes.map((s) => (
                          <span key={s} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--c-accentBg)", color: "var(--c-accentText)", fontWeight: 600 }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDate(key.createdAt)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDate(key.lastUsedAt)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDate(key.expiresAt)}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      <Pill label={key.status} colors={STATUS_COLORS[key.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    </td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {key.status === "active" && (
                        <button onClick={() => handleRevokeKey(key.id, key.name)}
                          style={{ fontSize: 12, padding: "3px 8px", borderRadius: 3, border: "none", background: "transparent", color: "var(--c-dangerText)", cursor: "pointer" }}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Webhooks ── */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Webhooks</div>
          <button onClick={() => { setError(null); setShowAddWebhook(true); }}
            style={{ ...btnPrimary, fontSize: 12, cursor: "pointer" }}>
            + Add Webhook
          </button>
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Receive real-time event notifications via HTTP POST to your endpoints.
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>
        ) : webhooks.length === 0 ? (
          <div style={{ ...emptyFrame, padding: "24px 0", fontSize: 13, color: theme.color.faint }}>
            No webhooks configured yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {webhooks.map((wh) => (
              <div key={wh.id} style={{ padding: "12px 14px", background: "var(--c-panelAlt)", borderRadius: 3, border: `1px solid ${theme.color.border}` }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontFamily: "monospace", color: theme.color.heading, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {wh.url}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {wh.events.map((ev) => (
                        <span key={ev} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--c-accentBg)", color: "var(--c-accentText)", fontWeight: 600 }}>
                          {ev}
                        </span>
                      ))}
                      {(wh.lastDeliveryStatus || testResults[wh.id]) && (() => {
                        const tr = testResults[wh.id];
                        const code = tr ? tr.statusCode : wh.lastDeliveryStatus;
                        const ok   = tr ? tr.success : (Number(wh.lastDeliveryStatus) >= 200 && Number(wh.lastDeliveryStatus) < 300);
                        return (
                          <span style={{ fontSize: 11, color: ok ? "var(--c-successText)" : "var(--c-dangerText)", fontWeight: 600 }}>
                            Last: {code ? `HTTP ${code}` : "no response"}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                    <button
                      onClick={() => handleTestWebhook(wh.id)}
                      disabled={!!testingWebhook[wh.id]}
                      style={{
                        fontSize: 11, padding: "4px 10px", borderRadius: 3,
                        border: "1px solid var(--c-border)", background: "var(--c-panel)",
                        color: theme.color.muted, cursor: testingWebhook[wh.id] ? "not-allowed" : "pointer",
                        fontWeight: 600, opacity: testingWebhook[wh.id] ? 0.6 : 1,
                      }}
                    >
                      {testingWebhook[wh.id] ? "Sending…" : "Send Test"}
                    </button>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: theme.color.muted }}>
                      <input type="checkbox" checked={wh.active} onChange={(e) => handleToggleWebhook(wh.id, e.target.checked)} />
                      Active
                    </label>
                    <button onClick={() => { setError(null); setEditingWebhook(wh); }}
                      style={{ fontSize: 12, padding: "3px 8px", borderRadius: 3, border: "none", background: "transparent", color: theme.color.accentText, cursor: "pointer" }}>
                      Edit
                    </button>
                    <button onClick={() => handleDeleteWebhook(wh.id, wh.url)}
                      style={{ fontSize: 12, padding: "3px 8px", borderRadius: 3, border: "none", background: "transparent", color: "var(--c-dangerText)", cursor: "pointer" }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      </>)}

      {/* ── Documentation ── */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Documentation</div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>Reference information for integrating with the Abacas Public API.</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Base URL</div>
            <code style={{ display: "block", padding: "8px 12px", background: "var(--c-panelAlt)", borderRadius: 3, fontSize: 12, color: theme.color.heading, fontFamily: "monospace" }}>
              https://api.abacassecurity.com/v1
            </code>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Authentication</div>
            <code style={{ display: "block", padding: "8px 12px", background: "var(--c-panelAlt)", borderRadius: 3, fontSize: 12, color: theme.color.heading, fontFamily: "monospace" }}>
              Authorization: Bearer {"<api-key>"}
            </code>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Available Endpoints</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[
                { method: "GET", path: "/v1/org",                  desc: "Organisation profile and settings" },
                { method: "GET", path: "/v1/compliance/summary",   desc: "Compliance score and control status summary" },
                { method: "GET", path: "/v1/risks",                desc: "Risk register entries" },
                { method: "GET", path: "/v1/documents",            desc: "Evidence repository documents" },
              ].map(({ method, path, desc }) => (
                <div key={path} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 0", borderBottom: `1px solid ${theme.color.border}` }}>
                  <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--c-accentBg)", color: "var(--c-accentText)", fontWeight: 700, flexShrink: 0, fontFamily: "monospace" }}>
                    {method}
                  </span>
                  <code style={{ fontSize: 12, color: theme.color.heading, fontFamily: "monospace", flexShrink: 0 }}>{path}</code>
                  <span style={{ fontSize: 12, color: theme.color.muted }}>{desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Webhook Signature Verification</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6, marginBottom: 6 }}>
              Each webhook request includes an <code style={{ background: "var(--c-panelAlt)", padding: "1px 4px", borderRadius: 3 }}>X-Abacas-Signature</code> header.
              Verify using HMAC-SHA256 with your signing secret:
            </div>
            <code style={{ display: "block", padding: "8px 12px", background: "var(--c-panelAlt)", borderRadius: 3, fontSize: 12, color: theme.color.heading, fontFamily: "monospace" }}>
              X-Abacas-Signature: sha256={"<hmac-sha256-of-body>"}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}
